import { ICalendarTimeAndRepeatValues } from '@gik/calendar/models/Calendar';
import { api } from '@gik/core/api';

import { StatusCodes } from 'http-status-codes';
import { BadRequest, UnhandledError } from '../HttpErrors';
import { IUpdateAnnouncementPayload } from './calendarAnnouncements';

const path = 'calendarEntries';

/**
 * Calendar Event payload model for creating or updating events
 */
export interface CreateUpdateEventPayload extends ICalendarTimeAndRepeatValues {
  inkindRouteId: string;

  typeId: number;
  title?: string; // only used by custom event types
  description?: string;

  allowGiftCards: boolean;
  numberRequired?: number;
  allowedServiceIds?: number[]; // claim type picker
  allowedGiftCardIds?: number[]; // gift card picker
  deliveryInstructions?: string; // for food and groceries
  emergencyContactInfo?: string; // for adult/seniour care, childcare and petcare this stores the emergency contact info
  pickupLocation?: string; // for transportation
  dropoffLocation?: string; // for transportation
  announcementTypeId?: number; // for announcements
  petCareTypeId?: number; // for pet care
}

export interface UnparentEventRequest {
  instanceDate: string; // set to modify an instance in a series rather than the entire series
}

export interface ModifyClaimedEventRequest {
  noteToClaimOwner?: string; // only required for modifying a claimed event
}

export interface CreateEventPayload extends CreateUpdateEventPayload {}

export interface UpdateEventPayload extends CreateUpdateEventPayload, UnparentEventRequest, ModifyClaimedEventRequest {}

export interface DeleteEventRequest extends ModifyClaimedEventRequest, UnparentEventRequest {}

export async function createEvent(payload: CreateEventPayload): Promise<string> {
  const response = await api.post(path, {
    json: payload,
  });

  if (response.status === StatusCodes.CREATED) {
    const location = response.headers.get('location');
    return location?.split('/').slice(-1)[0];
  } else {
    throw new UnhandledError(`Unexpected response code: ${response.status} ${response.statusText}`);
  }
}

export async function updateEvent(
  id: string,
  payload: UpdateEventPayload | IUpdateAnnouncementPayload
): Promise<boolean> {
  const response = await api.patch(`${path}/${id}`, {
    json: payload,
  });

  if (!response.ok) {
    console.error(response);
    switch (response.status) {
      case StatusCodes.BAD_REQUEST:
        throw new BadRequest('Failed to update request');
      default:
        throw new UnhandledError(`${response.status} (${response.statusText})`);
    }
  }
  return true;
}

export async function deleteEvent(id: string, payload?: DeleteEventRequest): Promise<boolean> {
  const result = await api.delete(`${path}/${id}`, {
    json: payload,
  });
  if (result.status !== StatusCodes.NO_CONTENT) {
    throw new UnhandledError(`Unexpected response code: ${result.status} ${result.statusText}`);
  }
  return true;
}
